import { useMemo } from 'react';
import {
  CHATBOT_CONVERSATIONS_PAGE_SIZES,
  type ChatbotConversationsPageSize,
} from '../../../types/userConfig';
import { useUserConfig } from '../../../userConfig/useUserConfig';
import { useChatbotConversationsListSearchParams } from './useChatbotConversationsListSearchParams';
import { useChatbotConversationsQuery } from '../queries/useChatbotConversationsQuery';
import type { ChatbotConversationWithIdentity } from '../../../types/chatbot';

function conversationActivityTime(
  conversation: ChatbotConversationWithIdentity,
): number {
  const candidates = [
    conversation.lastMessageAt,
    conversation.startedAt,
    conversation.created,
    conversation.stamp,
  ];
  for (const value of candidates) {
    if (!value) {
      continue;
    }
    const time = Date.parse(value);
    if (!Number.isNaN(time)) {
      return time;
    }
  }
  return 0;
}

export function useChatbotConversationsTab(
  chatbotId: string,
  enabled: boolean,
) {
  const {
    chatbotConversationsPageSize,
    setChatbotConversationsPageSize,
    isUserConfigReady,
  } = useUserConfig();
  const { page, setPage, listOptions } =
    useChatbotConversationsListSearchParams(chatbotConversationsPageSize);

  const conversationsQuery = useChatbotConversationsQuery(
    chatbotId,
    listOptions,
    enabled && isUserConfigReady,
  );

  const conversations = useMemo(() => {
    const items = conversationsQuery.data ?? [];
    return items
      .slice()
      .sort(
        (left, right) =>
          conversationActivityTime(right) - conversationActivityTime(left),
      );
  }, [conversationsQuery.data]);

  const hasPreviousPage = page > 1;
  const hasNextPage = conversations.length === chatbotConversationsPageSize;
  const isPageLoading =
    !isUserConfigReady || (enabled && conversationsQuery.isLoading);

  const handlePageSizeChange = (value: string) => {
    const parsedValue = Number(value);
    if (
      !CHATBOT_CONVERSATIONS_PAGE_SIZES.some((size) => size === parsedValue)
    ) {
      return;
    }
    setChatbotConversationsPageSize(
      parsedValue as ChatbotConversationsPageSize,
    );
    setPage(1);
  };

  return {
    conversationsQuery,
    conversations,
    page,
    setPage,
    hasPreviousPage,
    hasNextPage,
    chatbotConversationsPageSize,
    handlePageSizeChange,
    isPageLoading,
  };
}
